import { externalLinks } from 'src/externalLinks';
import { Identifier } from 'src/types/utilityTypes';
import { withAppBasePath } from 'src/utils/withAppBasePath';

export type CreatorPlanLink = {
  id: Identifier;
  slug?: string;
  usesNewCreator?: boolean | null;
};

export type CreatorDestination =
  | { kind: 'internal'; to: string }
  | { kind: 'external'; href: string };

export type ClientCalendarLink = {
  calendarClientId?: Identifier | null;
  id: Identifier;
};

const supportedCalendarQueryParams = new Set([
  'start',
  'end',
  'scrollToWeek',
  'addAlternate',
  'workout_log_error',
]);

const appendSearch = (path: string, search = ''): string => {
  if (!search) return path;

  const params = new URLSearchParams(search);
  const supported = new URLSearchParams();
  params.forEach((value, key) => {
    if (supportedCalendarQueryParams.has(key)) {
      supported.set(key, value);
    }
  });

  const query = supported.toString();
  return query ? `${path}?${query}` : path;
};

export const reactPlanEditHref = (planId: Identifier): string =>
  withAppBasePath(`/plans/${planId}/edit`);

export const reactPlanWorkoutHref = (
  planId: Identifier,
  workoutId: Identifier
): string => withAppBasePath(`/plans/${planId}/workout/${workoutId}`);

export const getPlanWorkoutHref = (
  plan: CreatorPlanLink,
  workoutId: Identifier,
  reactCreatorEnabled: boolean
): string => {
  if (reactCreatorEnabled && plan.usesNewCreator === true) {
    return reactPlanWorkoutHref(plan.id, workoutId);
  }

  if (plan.usesNewCreator === true) {
    return externalLinks.planWorkoutCreator(plan.id, workoutId);
  }

  return externalLinks.legacyPlanWorkout(plan.slug ?? plan.id, workoutId);
};

export const reactRoutineHref = (routineId: Identifier): string =>
  withAppBasePath(`/plans/routines/${routineId}/edit`);

export const reactNewRoutineHref = (): string =>
  withAppBasePath('/plans/routines/new');

export const reactCalendarHref = (
  calendarType: string,
  calendarId: Identifier,
  search = '',
  workoutId?: Identifier
): string => {
  const workoutPath = workoutId ? `/workout/${workoutId}` : '';
  return withAppBasePath(
    appendSearch(
      `/plans/calendar/${calendarType}/${calendarId}${workoutPath}`,
      search
    )
  );
};

export const classicCalendarHref = (
  calendarType: string,
  calendarId: Identifier,
  search = '',
  workoutId?: Identifier
): string => {
  const workoutPath = workoutId ? `/workout/${workoutId}` : '';
  return externalLinks.v3Url(
    appendSearch(
      `/calendar/${calendarType}/${calendarId}${workoutPath}`,
      search
    )
  );
};

export const getPlanEditDestination = (
  plan: CreatorPlanLink,
  reactCreatorEnabled: boolean
): CreatorDestination => {
  if (reactCreatorEnabled && plan.usesNewCreator === true) {
    return { kind: 'internal', to: `/plans/${plan.id}/edit` };
  }

  return {
    kind: 'external',
    href: externalLinks.workoutPlansEditUrl(
      plan,
      plan.usesNewCreator === true,
      reactCreatorEnabled
    ),
  };
};

export const getPlanEditHref = (
  plan: CreatorPlanLink,
  reactCreatorEnabled: boolean
): string => {
  const destination = getPlanEditDestination(plan, reactCreatorEnabled);

  return destination.kind === 'internal'
    ? withAppBasePath(destination.to)
    : destination.href;
};

export const getNewPlanDestination = (
  planKind: 'flex' | 'week',
  reactCreatorEnabled: boolean
): CreatorDestination =>
  reactCreatorEnabled
    ? { kind: 'internal', to: `/plans/new/${planKind}` }
    : {
        kind: 'external',
        href:
          planKind === 'flex'
            ? externalLinks.newFlexWorkoutPlan(false)
            : externalLinks.newWeekWorkoutPlan(false),
      };

export const getCalendarDestination = (
  calendarType: string,
  calendarId: Identifier,
  reactCreatorEnabled: boolean,
  search = '',
  workoutId?: Identifier
): CreatorDestination => {
  const workoutPath = workoutId ? `/workout/${workoutId}` : '';

  if (reactCreatorEnabled) {
    return {
      kind: 'internal',
      to: appendSearch(
        `/plans/calendar/${calendarType}/${calendarId}${workoutPath}`,
        search
      ),
    };
  }

  return {
    kind: 'external',
    href: classicCalendarHref(calendarType, calendarId, search, workoutId),
  };
};

export const getCalendarHref = (
  calendarType: string,
  calendarId: Identifier,
  reactCreatorEnabled: boolean,
  search = ''
): string => {
  const destination = getCalendarDestination(
    calendarType,
    calendarId,
    reactCreatorEnabled,
    search
  );

  return destination.kind === 'internal'
    ? withAppBasePath(destination.to)
    : destination.href;
};

export const getCalendarWorkoutHref = (
  calendarType: string,
  calendarId: Identifier,
  workoutId: Identifier,
  reactCreatorEnabled: boolean,
  search = ''
): string =>
  reactCreatorEnabled
    ? reactCalendarHref(calendarType, calendarId, search, workoutId)
    : classicCalendarHref(calendarType, calendarId, search, workoutId);

export const getClientCalendarHref = (
  client: ClientCalendarLink,
  reactCreatorEnabled: boolean,
  search = ''
): string | undefined => {
  if (!client.calendarClientId) return undefined;

  const destination = getCalendarDestination(
    'client',
    client.id,
    reactCreatorEnabled,
    search
  );

  return destination.kind === 'internal'
    ? withAppBasePath(destination.to)
    : destination.href;
};

export const getRoutineEditDestination = (
  routineId: Identifier,
  reactCreatorEnabled: boolean
): CreatorDestination =>
  reactCreatorEnabled
    ? { kind: 'internal', to: `/plans/routines/${routineId}/edit` }
    : { kind: 'external', href: externalLinks.editRoutine(routineId) };

export const getNewRoutineDestination = (
  reactCreatorEnabled: boolean
): CreatorDestination =>
  reactCreatorEnabled
    ? { kind: 'internal', to: '/plans/routines/new' }
    : { kind: 'external', href: externalLinks.newRoutine() };

export const getRoutineEditHref = (
  routineId: Identifier,
  reactCreatorEnabled: boolean
): string => {
  const destination = getRoutineEditDestination(
    routineId,
    reactCreatorEnabled
  );

  return destination.kind === 'internal'
    ? withAppBasePath(destination.to)
    : destination.href;
};

export const getNewRoutineHref = (reactCreatorEnabled: boolean): string => {
  const destination = getNewRoutineDestination(reactCreatorEnabled);

  return destination.kind === 'internal'
    ? withAppBasePath(destination.to)
    : destination.href;
};
